import Vue from 'vue';
import VueRouter from 'vue-router';
import authApi from '@/api/auth.api';
import { tryAuthWithCallback } from '@/api/apiClient';
import { browserNotSupported, isMobile, isIpad } from '@/helpers/browserSupport';
import { isMobileAppSso } from '@/helpers/mobileSso';
import { useRouterGuard } from '@/helpers/routerGuard';

import {
  videoLinkRoute,
  videoEditRoutes,
  reviewRedirectRoute,
  inAppRoutes,
  publicRoutes
} from './routes';

Vue.use(VueRouter);

const router = new VueRouter({
  mode: 'history',
  base: '/',
  routes: [
    videoLinkRoute,
    videoEditRoutes,
    reviewRedirectRoute,
    publicRoutes,
    inAppRoutes,
    { path: '*', redirect: '/home' }
  ],
  strict: true
});

const PATHS = {
  browserNotSupported: /\/browser_not_supported/,
  singleSignOn: /\/single-sign-on/,
  oldVideoFile: /\/share#!\/videoFile\//
};

async function logoutAndRedirect() {
  const accessToken = localStorage.getItem('accessToken');

  if (accessToken) {
    await authApi.logOut();
  }

  localStorage.clear();

  return {
    name: 'SignIn',
    replace: true
  };
}

function handleLegacyVideoUrl(to) {
  if (!PATHS.oldVideoFile.test(to.fullPath)) return null;

  const videoId = to.fullPath.split(PATHS.oldVideoFile)[1];

  return {
    name: 'VideoLink',
    params: { videoId },
    replace: true
  };
}

async function resolveAuth() {
  let isAuthorized = false;

  await tryAuthWithCallback((authorized) => {
    isAuthorized = authorized;
  });

  return isAuthorized;
}

router.beforeEach(async (to, from, next) => {
  const {
    skipBrowserNotSupported = false,
    mobileNotSupported = false,
    redirectIfAuthorized,
    publicPage
  } = to.meta || {};

  const matchBrowserNotSupported = PATHS.browserNotSupported.test(to.path);
  const matchSingleSignOn = PATHS.singleSignOn.test(to.path);

  // 1. Redirects

  // 1.1 legacy redirect
  const legacyRedirect = handleLegacyVideoUrl(to);
  if (legacyRedirect) {
    return next(legacyRedirect);
  }

  // 1.2 Single sign-on redirect
  // JIRA#DEV-8407
  // Need to deny access from mobile devices
  // Allow app-initiated SSO in both WebViews and external browsers.
  if (matchSingleSignOn && isMobileAppSso(to.query)) {
    return next();
  }

  // 2. Browser not supported
  const redirectToBrowserNotSupported = !skipBrowserNotSupported
    && !matchBrowserNotSupported
    && (browserNotSupported() || (mobileNotSupported && (isMobile() || isIpad())));

  if (redirectToBrowserNotSupported) {
    return next({
      name: 'BrowserNotSupported',
      query: { from: to.name },
      replace: true
    });
  }

  // 3. public / SSO / Role-Based Access Control
  const authorized = await resolveAuth();
  const { hasAccessToRoute, pathToRedirect } = useRouterGuard(to);

  if (publicPage && !(redirectIfAuthorized && authorized)) {
    return next();
  }

  // 4. logout
  if (to.path === '/logout') {
    const logoutParams = await logoutAndRedirect();
    return next(logoutParams);
  }

  // 5. auth
  if (redirectIfAuthorized && authorized) {
    return next({ path: pathToRedirect, replace: true });
  }

  if (!publicPage && !authorized) {
    const logoutParams = await logoutAndRedirect();
    return next(logoutParams);
  }

  // 6. Role-Based Access Control Check
  if (!hasAccessToRoute) {
    return next({
      path: pathToRedirect,
      query: { noAccess: true },
      replace: true
    });
  }

  // 7. root
  if (to.fullPath === '/') {
    return next({
      name: 'SignIn',
      replace: true
    });
  }

  // 8. legacy dashboard redirect (except contractor dashboard)
  if (to.path.includes('dashboard') && !to.path.includes('realync-studios')) {
    return next({
      name: 'Home',
      replace: true
    });
  }

  return next();
});

export default router;
